import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  combineLatest,
  concat,
  debounceTime,
  filter,
  map,
  merge,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';

import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';
import {
  activeSupplierIds,
  SupplierAllocation,
  SupplierAllocationsEditableContext,
} from '@ps/survey-utils';
import { SupplierApiActions, SupplierService } from '@ps/survey-data-access';

import { FeasibilityService } from '../../services/feasibility/feasibility.service';
import { FlexibilityPricesService } from '../../services/flexibility-prices/flexibility-prices.service';
import { SurveyScoreService } from '../../services/survey-score/survey-score.service';

import {
  SurveyCreationActions,
  SurveyCreationApiActions,
} from '../audience/survey-creation-audience.actions';
import {
  selectMetadataForm,
  selectSurvey,
  selectSurveyFormState,
  selectSurveyId,
} from '../audience/survey-creation-audience.selectors';

import { SurveyForm } from '../../utils';
import {
  computeFeasibilityParams,
  qualifiesForRecalculation,
  shouldMarkAsPending,
} from './utils/survey-creation-recalculation.helper';
import { RecalculationData } from './utils/survey-creation-recalculation.types';

import {
  SurveyCreationInsightsActions,
  SurveyCreationInsightsApiActions,
} from './survey-creation-insights.actions';
import {
  selectAdvancedFilters,
  selectAdvancedFilterSurveyGroups,
} from './survey-creation-insights.selectors';

@Injectable()
export class SurveyCreationInsightsEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _surveyScoreService = inject(SurveyScoreService);
  private readonly _feasibilityService = inject(FeasibilityService);
  private readonly _supplierService = inject(SupplierService);
  private readonly _flexibilityPricesService = inject(FlexibilityPricesService);

  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);

  public readonly computeScore$ = createEffect(() => {
    const surveyChangeTrigger$ = this._actions$.pipe(
      ofType(
        SurveyCreationActions.updateMetadata,
        SurveyCreationActions.toggleManualProcessing,
        SurveyCreationApiActions.loadSurveyForEditSuccess,
        SurveyCreationApiActions.saveSurveySuccess,
      ),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
    );

    return merge(
      surveyChangeTrigger$.pipe(
        filter(([action]) => shouldMarkAsPending(action)),
        map(() => SurveyCreationInsightsApiActions.computeScorePending()),
      ),
      surveyChangeTrigger$.pipe(
        filter(([action]) =>
          qualifiesForRecalculation(
            action,
            !!this._userSettingsSelectors.buyerSettings()
              .enableManualProcessing,
          ),
        ),
        debounceTime(500),
        map(([, surveyId]) => Option.from(surveyId).unwrap()),
        concatLatestFrom(() => this._store.select(selectMetadataForm)),
        filter(([surveyId, metadata]) => !!surveyId && !!metadata?.valid),
        map(([surveyId, metadata]) => ({
          surveyId,
          value: Option.from(metadata.value).map((value) => ({
            loi: Option.from(value.loi).unwrapOr(0),
            incidence: Option.from(value.incidence).unwrapOr(0),
          })),
        })),
        filter(({ value }) => value.isSome()),
        map(({ surveyId, value }) => ({ surveyId, value: value.unwrap() })),
        switchMap(({ surveyId, value }) =>
          this._surveyScoreService
            .computeScore(surveyId, {
              // TODO: Update CPI when we have Flexibility Prices
              cpi: 0, // Price Override OR ACPI OR 0
              ir: value.incidence,
              loi: value.loi,
            })
            .pipe(
              map(({ score, scorePercentage }) =>
                SurveyCreationInsightsApiActions.computeScoreSuccess({
                  score,
                  scorePercentage,
                }),
              ),
              this.psCatchError((error) =>
                of(
                  SurveyCreationInsightsApiActions.computeScoreFailure({
                    error: error.message,
                  }),
                ),
              ),
            ),
        ),
      ),
    );
  });

  public readonly recalculateFeasibility$ = createEffect(() => {
    const surveyChangeTrigger$ = this._actions$.pipe(
      ofType(
        SurveyCreationApiActions.loadSurveyForEditSuccess,
        SurveyCreationApiActions.saveSurveySuccess,
        SurveyCreationActions.updateMetadata,
        SurveyCreationActions.updateQualifications,
        SurveyCreationActions.updateQuotas,
        SurveyCreationActions.toggleManualProcessing,
        SurveyCreationInsightsActions.recalculateFeasibility,
      ),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
    );

    return merge(
      surveyChangeTrigger$.pipe(
        filter(([action]) => shouldMarkAsPending(action)),
        map(() =>
          SurveyCreationInsightsApiActions.recalculateFeasibilityPending(),
        ),
      ),
      surveyChangeTrigger$.pipe(
        filter(([action]) =>
          qualifiesForRecalculation(
            action,
            !!this._userSettingsSelectors.buyerSettings()
              .enableManualProcessing,
          ),
        ),
        debounceTime(500),
        map(([, surveyId]) => Option.from(surveyId).unwrap()),
        concatLatestFrom(() => [
          this._store.select(selectSurveyFormState),
          this._store.select(selectSurvey),
        ]),
        map(([surveyId, formState, survey]) => ({
          surveyId,
          survey: Option.from(survey),
          form: SurveyForm.from(formState),
        })),
        filter(({ form }) => form.valid),
        map(({ surveyId, survey, form }) => ({
          user: this._userData(),
          surveyId,
          survey: Option.from(survey),
          ...Option.from(form.data).unwrap(),
        })),
        switchMap((data) => this._recalculateFlexibilityPricing$(data)),
      ),
    );
  });

  // Reacts to the update PUT landing. Its response already carries the updated
  // allocations (and the reducer has already stored them), so this re-runs only
  // flexibility → feasibility, reading the allocations straight off the action
  // rather than refetching them or reaching into the slice.
  public readonly refreshPricingAfterSupplierUpdate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierApiActions.updateAllocationsSuccess),
      concatLatestFrom(() => [
        this._store.select(selectSurveyId),
        this._store.select(selectSurveyFormState),
        this._store.select(selectSurvey),
      ]),
      filter(([, surveyId]) => !!surveyId),
      map(([{ allocations }, surveyId, formState, survey]) => ({
        allocations,
        surveyId: Option.from(surveyId).unwrap(),
        survey: Option.from(survey),
        form: SurveyForm.from(formState),
      })),
      filter(({ form }) => form.valid),
      map(({ allocations, surveyId, survey, form }) => ({
        allocations,
        data: {
          user: this._userData(),
          surveyId,
          survey: Option.from(survey),
          ...Option.from(form.data).unwrap(),
        },
      })),
      switchMap(({ data, allocations }) =>
        concat(
          of(SurveyCreationInsightsApiActions.recalculateFeasibilityPending()),
          this._loadFlexibilityPrices$(data, allocations),
        ),
      ),
    ),
  );

  private _recalculateFlexibilityPricing$(
    data: RecalculationData,
  ): Observable<Action> {
    return this._supplierService
      .allocations(data.surveyId, {
        scope: 'editable',
        context: this._toSupplierContext(data),
      })
      .pipe(
        switchMap((allocations) =>
          concat(
            of(SupplierApiActions.loadAllocationsSuccess({ allocations })),
            this._loadFlexibilityPrices$(data, allocations),
          ),
        ),
        this.psCatchError(
          (error) =>
            of(
              SupplierApiActions.loadAllocationsFailure({
                error: error.message ?? 'Failed to load supplier allocations',
              }),
            ),
          { notify: true },
        ),
      );
  }

  private _loadFlexibilityPrices$(
    data: RecalculationData,
    allocations: readonly SupplierAllocation[],
  ): Observable<Action> {
    return this._flexibilityPricesService.get(data.surveyId).pipe(
      switchMap((prices) =>
        concat(
          of(
            SurveyCreationInsightsApiActions.loadFlexibilityPricesSuccess({
              prices,
            }),
          ),
          this._computeFeasibility$(data, allocations),
        ),
      ),
      this.psCatchError(
        (error) =>
          of(
            SurveyCreationInsightsApiActions.loadFlexibilityPricesFailure({
              error: error.message ?? 'Failed to load flexibility prices',
            }),
          ),
        { notify: true },
      ),
    );
  }

  /**
   * Feasibility recalculation flow, one recalculation trigger in:
   *
   * 1. Always fire the default request → `recalculateFeasibilitySuccess`/`Failure`.
   * 2. When `withExclusions` is present, fire it too, in parallel via `merge` (same
   *    endpoint, different body) → `recalculateFeasibilityWithExclusionsSuccess`/`Failure`.
   *
   * Both requests, when both fire, land in different `FeasibilityState` fields — neither
   * overwrites the other. This lets the exclusion checkbox (`toggleExcludeFromFeasibleAudience`)
   * swap which one is displayed (`selectDisplayedFeasibility`) without a new network call.
   */
  private _computeFeasibility$(
    data: RecalculationData,
    allocations: readonly SupplierAllocation[],
  ): Observable<Action> {
    return combineLatest([
      this._store.select(selectAdvancedFilters),
      this._store.select(selectAdvancedFilterSurveyGroups),
    ]).pipe(
      take(1),
      map(([advancedFilters, advancedFilterSurveyGroups]) =>
        computeFeasibilityParams(
          data,
          advancedFilters,
          advancedFilterSurveyGroups,
          activeSupplierIds(allocations),
        ),
      ),
      switchMap(({ base, withExclusions }) => {
        const defaultRequest$ = this._feasibilityService.compute(base).pipe(
          map(SurveyCreationInsightsApiActions.recalculateFeasibilitySuccess),
          this.psCatchError((error) =>
            of(
              SurveyCreationInsightsApiActions.recalculateFeasibilityFailure({
                error: error.message,
              }),
            ),
          ),
        );

        if (!withExclusions) {
          return defaultRequest$;
        }

        return merge(
          defaultRequest$,
          this._feasibilityService.compute(withExclusions).pipe(
            map(
              SurveyCreationInsightsApiActions.recalculateFeasibilityWithExclusionsSuccess,
            ),
            this.psCatchError((error) =>
              of(
                SurveyCreationInsightsApiActions.recalculateFeasibilityWithExclusionsFailure(
                  { error: error.message },
                ),
              ),
            ),
          ),
        );
      }),
    );
  }

  private _toSupplierContext(
    data: RecalculationData,
  ): SupplierAllocationsEditableContext {
    return {
      loi: data.metadata.loi ?? 0,
      ir: data.metadata.incidence ?? 0,
      languageCode: data.survey.mapOr(
        '',
        (survey) => survey.languageCode ?? '',
      ),
      fieldTime: data.metadata.fieldTime ?? 0,
      goal: data.metadata.completes ?? 0,
      clickBalancing: data.metadata.clickBalancing,
    };
  }

  private _userData(): { id: number; email: string; companyId: number } {
    const user = this._authSelectors.user().unwrap();
    return {
      id: user.id,
      email: user.email,
      companyId: this._authSelectors.activeCompanyId(),
    };
  }
}
